//----------------------------------------
// スキャン用の常駐コンテナ
//----------------------------------------
// scandit/sdk.jsx の host と同じ考え方。
// カメラ描画先のDOMをbody直下に常駐させ、スキャン画面が表示されたときだけ
// 画面内のスロットへ付け替える。これによりスキャン画面が未表示でもカメラを起動できる。
//
// ※display:none にすると端末によっては映像が停止するため、
// 　非表示時も「小さく・透明・最背面」でレンダリングは継続させる

import { CAMERA_FOCUS_AUTO, getExplicitFocusDistance } from "./cameraFocusSetting";

const HIDDEN_STYLE =
	"position:fixed;left:0;top:0;width:160px;height:120px;z-index:-1;opacity:0;pointer-events:none;overflow:hidden;background-color:#000;";

const VISIBLE_STYLE =
	"position:absolute;top:0;left:0;width:100%;height:100%;z-index:0;overflow:hidden;background-color:#000;";

//直近の play() の失敗理由と reviveVideos() の結果
//※プレビューが出ないときの切り分けに使う（describePreviewState で出力する）
let lastPlayError = null;
let lastReviveResult = "-";

//----------------------------------------
//常駐コンテナ生成（body直下・非表示
//----------------------------------------

export function createHost(id) {
	const exists = document.getElementById(id);

	//既にある場合はスタイルを触らない
	//※スキャン画面へ付け替え済み（表示中）のときに非表示スタイルへ戻さないため
	if (exists) {
		return exists;
	}

	const host = document.createElement("div");
	host.id = id;
	host.style.cssText = HIDDEN_STYLE;
	document.body.appendChild(host);

	return host;
}

//----------------------------------------
//スキャン画面のスロットへ付け替え（表示
//----------------------------------------

export function attachHost(host, slot) {
	if (!host || !slot) return;

	host.style.cssText = VISIBLE_STYLE;

	if (host.parentElement !== slot) {
		slot.appendChild(host);
	}

	//DOM移動でvideoが一時停止する端末があるため再生し直す
	playVideos(host);
}

//----------------------------------------
//body直下へ戻す（非表示・カメラは保持
//----------------------------------------
// 画面遷移アニメーション中は次の画面が先にマウントされるため、
// 自分のスロットに入っている場合のみ戻す（次の画面から奪わない）

export function detachHost(host, slot) {
	if (!host) return;
	if (slot && host.parentElement !== slot) return;

	host.style.cssText = HIDDEN_STYLE;
	document.body.appendChild(host);

	playVideos(host);
}

//----------------------------------------
//表示中のまま一時的に見えなくする
//----------------------------------------
// カメラ再取得中は映像が無く、iOSではvideoにネイティブの再生ボタンが表示されるため、
// フレームが届くまで透明にしておく。
// ※display:none や visibility:hidden と違い、描画は継続されるので映像の再開を妨げない

export function setHostTransparent(host, transparent) {
	if (!host) return;
	host.style.opacity = transparent ? "0" : "1";
}

//----------------------------------------
//表示スタイルの当て直し
//----------------------------------------
// スキャン画面のスロットへ付け替えた直後にスタイルが確定していない場合があるため、
// 映像が出たことを確認できた時点で当て直す。
//
// ※以前はここで「window の resize 発火」と「コンテナ幅の1px変更」を行い、
// 　SDKにプレビューのレイアウト再計算をさせていたが、次の2点により取りやめた。
// 　・映像が小さくなる問題（D-21）には効果が無かった。実際に効くのはスキャナーの再起動
// 　・スキャン枠（.scanCover）が約0.25秒消える副作用が出た。
// 　　.scanCover は translate3d で独立した合成レイヤーになっており背景がSVGのため、
// 　　リサイズを受けると再ラスタライズが走り、iOSでは再デコード中に描画が空になる

export function refreshHostLayout(host) {
	if (!host) return;

	//退避中（body直下）は対象外。スキャン画面に付いているときだけ行う
	if (!host.parentElement || host.parentElement === document.body) return;

	host.style.cssText = VISIBLE_STYLE;
	playVideos(host);
}

//----------------------------------------
//停止したvideoにストリームを付け直す
//----------------------------------------
// 常駐コンテナをスキャン画面のスロットへ付け替えると、端末によっては
// video要素が素材を手放し、readyState が 0（HAVE_NOTHING）のまま戻らない。
// MediaStream 自体は生きている（トラックは live）ので、同じstreamを
// 付け直せば復帰する。playVideos() の play() だけでは、
// 読み込んでいる素材が無いため戻らない。
//
// ※srcObject を null にしたまま放置するとSDK内部の参照が崩れるが、
// 　ここは同じ処理内で必ず同じstreamを入れ直すため、SDK側の参照には影響しない。
// 　触っているのはvideo要素だけで、SDKが持つ mediaStream / videoTrack は変えていない

export function reviveVideos(el) {
	if (!el) return false;

	let revived = false;

	const videos = el.querySelectorAll("video");
	if (videos.length === 0) lastReviveResult = "no-video";

	videos.forEach((video) => {
		//まだ素材を持っているものは触らない
		if (video.readyState !== 0) {
			lastReviveResult = "ready-ok";
			return;
		}

		const stream = video.srcObject;
		if (!stream || typeof stream.getVideoTracks !== "function") {
			lastReviveResult = "no-stream";
			return;
		}

		//トラックが死んでいる場合は付け直しても意味がない（カメラの取り直しが必要
		const track = stream.getVideoTracks()[0];
		if (!track || track.readyState !== "live") {
			lastReviveResult = "track-" + (track ? track.readyState : "none");
			return;
		}

		try {
			video.srcObject = null;
			video.srcObject = stream;
			revived = true;
			lastReviveResult = "done";
		} catch (e) {
			lastReviveResult = "error";
			console.log("reviveVideos error: " + e);
		}
	});

	if (revived) {
		playVideos(el);
	}

	return revived;
}

//----------------------------------------
//プレビューが出ない原因の切り分け用の文字列
//----------------------------------------
// プレビュー待ちがタイムアウトしたときに、何が起きていないのかを載せる。
// 判定条件は「スロット内にvideoがある」「readyState>=3」「videoWidth>0」の3つだが、
// これだけでは原因が絞れなかったため、判別に使える値をまとめて出す。
//
//   videos=0        → 常駐コンテナにvideoが無い（SDKがまだ作っていない）
//   videos=2以上    → 古いvideoが残っている。先頭を見ているため誤判定の可能性あり
//   hostInSlot=no   → コンテナが body 直下に退避したまま（付け替えの問題）
//   stream=no       → videoにMediaStreamが付いていない（付け直しの対象にならない）
//   track=ended     → トラックが死んでいる。カメラの取り直しが必要
//   paused=yes      → 再生されていない。play=... に拒否理由が入る
//   play=NotAllowedError → 自動再生ポリシーで拒否されている
//   revive=...      → reviveVideos() が何をしたか（done / no-stream / track-... / ready-ok）
//
// ※実機（WebView）ではログを取れないことがあるため、エラーモーダルにも出す

export function describePreviewState(slot, containerId) {
	try {
		const host = document.getElementById(containerId);
		const videos = slot ? slot.querySelectorAll("video") : [];
		const video = videos[0];
		const stream = video?.srcObject;
		const track = stream?.getVideoTracks?.()[0];

		return [
			"videos=" + videos.length,
			"hostInSlot=" + (host && slot && host.parentElement === slot ? "yes" : "no"),
			"ready=" + (video ? video.readyState : "-"),
			"size=" + (video ? video.videoWidth + "x" + video.videoHeight : "-"),
			"paused=" + (video ? (video.paused ? "yes" : "no") : "-"),
			"stream=" + (stream ? "yes" : "no"),
			"track=" + (track ? track.readyState : "-"),
			"inline=" + (video ? (video.playsInline ? "yes" : "no") : "-"),
			"muted=" + (video ? (video.muted ? "yes" : "no") : "-"),
			"revive=" + lastReviveResult,
			"play=" + (lastPlayError ? (lastPlayError.name || String(lastPlayError)) : "-"),
		].join(" ");
	} catch (e) {
		return "state error: " + e;
	}
}

//----------------------------------------
//カメラの能力をログ出力（1回だけ
//----------------------------------------
// バーコードにピントが合うまでが遅い件の切り分け用。
// AFを自動制御できるかどうかは「端末のカメラHAL × WebViewのChromeのバージョン」で決まり、
// 機種名からは判断できないため、実機のログで確認できるようにする。
//
// focusMode の中身で対策が変わる。
// ・continuous を含む  → 継続AFあり。定期的な再フォーカスは合っているピントを外すので逆効果
// ・continuous 無し / single-shot あり → 定期的に single-shot を撃つ対策が有効（scanditと同じ）
// ・undefined          → applyConstraints でのAF制御自体ができない。ROIやズームで攻めるしかない
//
// focusDistance.min も併せて出す。barkoderは内部に「20cm以内に寄れないカメラなら
// 別レンズへ切り替える」補正を持つが、カメラピッカー無効＋cameraId明示指定のため発動しない。
// ここが原因かどうかの判断に使う。
//
// ※ストリームは startScanner / createBarcodeScanner の直後にはまだ上がっていないため、
// 　トラックが live になるまで待ってから読む

const CAPABILITIES_TIMEOUT = 3000;
const CAPABILITIES_INTERVAL = 100;

let capabilitiesLogged = false;

//----------------------------------------
//liveなトラックが取れるまで待つ
//----------------------------------------
// ストリームは startScanner / createBarcodeScanner の直後にはまだ上がっていない。

async function waitForVideoTrack(host) {
	const limit = Date.now() + CAPABILITIES_TIMEOUT;

	while (true) {
		const video = host?.querySelector("video");
		const stream = video?.srcObject;
		const track = stream?.getVideoTracks?.()[0];
		if (track && track.readyState === "live") return track;

		if (Date.now() >= limit) return null;
		await new Promise((resolve) => setTimeout(resolve, CAPABILITIES_INTERVAL));
	}
}

export async function logCameraCapabilities(host, label) {
	if (capabilitiesLogged || !host) return;

	const track = await waitForVideoTrack(host);
	if (!track) {
		console.log("【" + label + "】camera capabilities: トラックを取得できず");
		return;
	}

	//トラックが取れた時点で以降は出さない（取れなかった場合は次の起動で再挑戦する
	capabilitiesLogged = true;

	if (typeof track.getCapabilities !== "function") {
		console.log("【" + label + "】camera capabilities: getCapabilities 非対応");
		return;
	}

	let caps = null;
	let settings = {};
	try {
		caps = track.getCapabilities();
		if (typeof track.getSettings === "function") settings = track.getSettings();
	} catch (e) {
		console.log("【" + label + "】getCapabilities error: " + e);
		return;
	}

	console.log("-------------------------");
	console.log("【" + label + "】camera capabilities");
	console.log("camera: " + track.label);
	console.log("focusMode: " + JSON.stringify(caps?.focusMode));
	console.log("focusDistance: " + JSON.stringify(caps?.focusDistance));
	console.log("zoom: " + JSON.stringify(caps?.zoom));
	console.log("torch: " + JSON.stringify(caps?.torch));
	console.log("stream: " + settings.width + "x" + settings.height + " / " + settings.frameRate + "fps");
	//能力として持っていても、実際に適用されているとは限らないため実値も出す
	console.log("applied focusMode: " + settings.focusMode);
	console.log("applied focusDistance: " + settings.focusDistance);
	console.log("-------------------------");
}

//----------------------------------------
//フォーカスの固定
//----------------------------------------
// フォーカスはSDKの機能ではなく、ブラウザのカメラAPI（MediaStreamTrack）の層で制御する。
// barkoder / scanbot はどちらもフォーカスを操作するAPIを持たず、
// applyConstraints を torch と zoom にしか使っていないため、こちらで指定する。
//
//   track.applyConstraints({ advanced: [{ focusMode: "manual", focusDistance: n }] })
//
// ※できるかどうかは端末が決める。getCapabilities().focusMode に manual が無ければ何もしない
// ※トラックが作り直されると設定は消えるため、カメラの起動・再起動のたびに呼ぶこと
//
// NEAR は focusDistance を最も近い側（＝range.min）へ寄せる（単焦点固定）。
// focusDistance の単位はメートル。指定値と適用後の実値はログへ出す。

//適用の再試行（スキャナーの組み直しと重なると一時的に失敗するため
const FOCUS_APPLY_RETRY = 3;
const FOCUS_APPLY_RETRY_WAIT = 300;

//manual を当てる前に待つ時間
//※カメラを取り直した直後にいきなり manual にすると、レンズが動く前に
//　その位置で凍結してしまうことがある。継続AFに一度合わせさせてから固定する。
//　初回起動時（スタート画面でカメラが動いている）は成功し、
//　スキャン画面を出入りしてカメラを取り直すと失敗しやすい、という出方の説明になる。
//　この待ちのあいだに次の適用が始まった場合は、世代番号で古いほうが降りるため、
//　連続した呼び出しのうち最後の1回だけが実際に適用される
const FOCUS_SETTLE_WAIT = 800;

//適用処理の世代（後から呼ばれたら古い処理は打ち切る
let focusGeneration = 0;

//そのトラックが今も使われているか
//※スキャナーを組み直すとトラックが差し替わる。古いトラックに対して
//　applyConstraints を続けても失敗するだけで、
//　OperationError: The associated Track is in an invalid state などのノイズになる
function isTrackCurrent(host, track) {
	if (!track || track.readyState !== "live") return false;

	const current = host?.querySelector("video")?.srcObject?.getVideoTracks?.()[0];
	return current === track;
}

//指定した値が実際に効いたか
//※advanced の制約は満たせないと黙って無視されるため、getSettings() で突き合わせる。
//　focusDistance を報告しない端末では判定できないので null を返す（その場合は据え置く）
function isFocusApplied(track, distance) {
	if (typeof track.getSettings !== "function") return null;

	const settings = track.getSettings();
	if (settings.focusMode !== "manual") return false;
	if (typeof settings.focusDistance !== "number") return null;

	//端末が step 単位に丸めるため、多少のズレは許容する
	return Math.abs(settings.focusDistance - distance) <= Math.max(0.05, Math.abs(distance) * 0.1);
}

//指定方法を変えながら適用を試みる
//※端末によって受け付ける形が違い、しかも受け付けない場合は例外ではなく無視で返るため
async function tryApplyFocus(track, distance) {
	const patterns = [
		//まとめて指定
		{ advanced: [{ focusMode: "manual", focusDistance: distance }] },
		//分けて指定（scandit と同じ形
		[{ advanced: [{ focusMode: "manual" }] }, { advanced: [{ focusDistance: distance }] }],
		//advanced を使わず指定
		{ focusMode: "manual", focusDistance: distance },
	];

	for (const pattern of patterns) {
		try {
			const list = Array.isArray(pattern) ? pattern : [pattern];
			for (const constraints of list) {
				await track.applyConstraints(constraints);
			}
		} catch (e) {
			continue;
		}

		const applied = isFocusApplied(track, distance);
		//判定できない端末は「効いたかもしれない」ので、そこで打ち切る
		if (applied === null || applied === true) return applied;
	}

	return false;
}

export async function applyCameraFocus(host, label, focus) {
	if (!host) return false;
	if (focus === CAMERA_FOCUS_AUTO) return false;

	const generation = ++focusGeneration;

	const track = await waitForVideoTrack(host);
	if (!track) {
		console.log("【" + label + "】focus: トラックを取得できず");
		return false;
	}

	//待っているあいだに次の適用が始まっていたら、こちらは用済み
	if (generation !== focusGeneration) return false;

	//レンズが動く前に固定してしまわないよう、少し待つ（上の定数のコメントを参照
	await new Promise((resolve) => setTimeout(resolve, FOCUS_SETTLE_WAIT));

	if (generation !== focusGeneration || !isTrackCurrent(host, track)) {
		console.log("【" + label + "】focus: 後続の適用があるため中止");
		return false;
	}

	if (typeof track.getCapabilities !== "function" || typeof track.applyConstraints !== "function") {
		console.log("【" + label + "】focus: この端末では制御できない（API非対応）");
		return false;
	}

	try {
		const caps = track.getCapabilities();
		const modes = caps?.focusMode;
		const range = caps?.focusDistance;

		if (!Array.isArray(modes) || modes.indexOf("manual") === -1) {
			console.log("【" + label + "】focus: この端末では固定できない（manual非対応）");
			return false;
		}
		if (!range || typeof range.min !== "number" || typeof range.max !== "number") {
			console.log("【" + label + "】focus: この端末では固定できない（focusDistanceなし）");
			return false;
		}

		//数値指定（?focus=0.3 など）は両端ではなくその値をそのまま使う。
		//両端では合わない端末があるため、実機で詰められるようにしてある
		const explicit = getExplicitFocusDistance(focus);

		//※focusDistance の単位は「メートル」。W3C仕様どおりで、min が最短。
		//　実機（DIGNO BX3 / range {min:0.08, max:5}）での総当たりで確定した。
		//　　0.08（8cm）  → 毎回スキャンOK。最も良好
		//　　0.2 （20cm） → 毎回スキャンOK
		//　　5   （5m）   → 認識しない
		//　一時ジオプター（1/m）と判断して max を最短に割り当てていたが、これは誤り。
		//　根拠にした「継続AF時の focusDistance が 0」は、単に未設定を意味していたとみられる。
		//　NEAR は range.min（その端末が寄れる限界）
		const distance = explicit !== null
			? Math.min(range.max, Math.max(range.min, explicit))
			: range.min;

		console.log("【" + label + "】focus: " + focus + " → focusDistance " + distance +
			"（range " + range.min + "〜" + range.max + " step " + range.step + "）");

		//スキャナーの組み直しと重なるとトラックが差し替わり、
		//applyConstraints が UnknownError: setPhotoOptions failed で落ちることがある。
		//一時的な失敗なので少し置いてやり直す
		let applied = false;
		for (let i = 0; i < FOCUS_APPLY_RETRY; i++) {
			//差し替え済みのトラックに対して続けても失敗するだけなので、静かに降りる。
			//このあと新しいトラックに対して呼び直されるため、ここで戻す必要もない
			if (generation !== focusGeneration || !isTrackCurrent(host, track)) {
				console.log("【" + label + "】focus: トラックが差し替わったため中止");
				return false;
			}

			applied = await tryApplyFocus(track, distance);
			if (applied === null || applied === true) break;

			if (i < FOCUS_APPLY_RETRY - 1) {
				await new Promise((resolve) => setTimeout(resolve, FOCUS_APPLY_RETRY_WAIT));
			}
		}

		const settings = typeof track.getSettings === "function" ? track.getSettings() : {};

		if (applied === false) {
			//manual だけ効いて距離が入らないと、レンズがその位置で固定され
			//「どの距離でもボケたまま」になる。AUTOより悪い状態なので必ず戻す。
			//※ただし今も使われているトラックに限る
			if (!isTrackCurrent(host, track)) {
				console.log("【" + label + "】focus: トラックが差し替わったため中止");
				return false;
			}

			console.log("【" + label + "】focus: focusDistance が適用されないため継続AFへ戻す");
			try {
				await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
			} catch (e) {
				console.log("【" + label + "】focus: 継続AFへの復帰に失敗 " + e);
			}
			return false;
		}

		console.log("【" + label + "】focus applied: mode=" + settings.focusMode +
			" distance=" + settings.focusDistance +
			(applied === null ? "（端末が実値を報告しないため未検証）" : ""));
		return true;
	} catch (e) {
		console.log("【" + label + "】focus error: " + e);
		return false;
	}
}

//----------------------------------------
//配下のvideoを再生（DOM移動対策
//----------------------------------------

export function playVideos(el) {
	if (!el) return;

	el.querySelectorAll("video").forEach((video) => {
		if (video.paused) {
			const played = video.play();
			if (played && typeof played.catch === "function") {
				//※握り潰すと自動再生ポリシーで拒否された場合に気付けないため、理由だけ残す
				played.catch((e) => {
					lastPlayError = e;
				});
			}
		}
	});
}
